import Vue from 'vue';
import {validationMixin} from "vuelidate";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import {makeReactiveBase} from "../start-ui";
import {scrollToElement} from "../helper";

export const install = ({store, router}) => ({
    beforeCreate() {
        let options = this.$options;

        if (!(options.form instanceof Object)) {
            return;
        }

        let component = this;
        let beforeEachHandler;
        let config = Object.assign({
            getter: null,
            action: null,
            prepareInitial: data => data,
            prepareSubmit: data => data,
            afterSubmit: () => true,
            isValid: () => !this.$v.$anyError,
            handleError: (e) => console.error(e),
            preventUnload: true,
            unloadMessage: null,
            scrollToError: true,
            scrollToErrorOffset: 0,
            cleanSubmit: false,
            autoInit: true,
            validations: {},
        }, options.form);


        const beforeUnload = event => {
            if (config.preventUnload && vm.isDirty) {
                event.returnValue = config.unloadMessage || this.__('form/unsaved_body');
            }
        };

        const beforeRouteLeave = (to, from, next) => {
            if (!config.preventUnload || !vm.isDirty) {
                return next();
            }
            if (!this.$modal) {
                if (window.confirm(config.unloadMessage || this.__('form/unsaved_body'))) {
                    next();
                }
                return;
            }

            if (!this.$modal.exists('form-unsaved')) {
                this.$modal.register('form-unsaved', require('../components/modals/FormSaveModal').default);
            }

            this.$modal.open('form-unsaved', {
                message: config.unloadMessage,
                callback: async action => {
                    if (action === 'discard') {
                        next();
                    } else if (action === 'save') {
                        await vm.$submit();
                        if (!this.$v.$anyError) {
                            next();
                        }
                    }
                },
            });
        };

        let vm = makeReactiveBase({
            mixins: [validationMixin],

            data() {
                return {
                    isDirty: false,
                    isSaving: false,
                    isSaved: false,
                    currentData: {},
                    persistedData: {},
                };
            },

            validations: {
                currentData: {},
            },

            methods: {
                $init() {
                    let data = {};
                    if (config.getter !== null) {
                        if (config.getter instanceof Function) {
                            data = config.getter.call(component);
                        } else if (config.getter instanceof Array) {
                            let [getter, payload] = config.getter;
                            if (payload instanceof Function) {
                                payload = payload.call(component);
                            }
                            data = store.getters[getter](payload);
                        } else {
                            data = store.getters[config.getter];
                        }
                    }

                    data = config.prepareInitial.call(component, cloneDeep(data || {}));
                    Vue.set(this.$data, 'persistedData', cloneDeep(data));
                    Vue.set(this.$data, 'currentData', cloneDeep(data));
                    Vue.set(this.$data, 'isDirty', false);
                    Vue.set(this.$data, 'isSaving', false);
                },

                async $submit(withValidation = true) {
                    // remove focus from any form input before submit
                    let el = document.querySelector(':focus');
                    if (el) {
                        el.blur();
                    }

                    if (withValidation) {
                        this.$v.$touch();
                        if (!config.isValid.call(component)) {
                            if (config.scrollToError && this.$ui) {
                                await this.$nextTick();
                                scrollToElement(component.$el.querySelector('.alert-danger, .has-error'), config.scrollToErrorOffset);
                            }
                            return;
                        }
                    } else {
                        this.$v.$reset();
                    }

                    if (!config.cleanSubmit && !this.isDirty) {
                        // prevent submit if form is not dirty
                        return;
                    }

                    Vue.set(this.$data, 'isSaving', true);
                    let data = config.prepareSubmit.call(component, cloneDeep(this.currentData));
                    let result;
                    try {
                        if (config.action instanceof Function) {
                            result = await config.action.call(component, data);
                        } else {
                            result = await store.dispatch(config.action, data);
                        }
                        if (await config.afterSubmit.call(component, result) !== false) {
                            this.$init();
                        }
                        Vue.set(this.$data, 'isSaved', true);
                    } catch (e) {
                        config.handleError.call(component, e);
                    }
                    Vue.set(this.$data, 'isSaving', false);
                },
            },

            render: () => null, // dummy

            watch: {
                currentData: {
                    handler() {
                        let dirty = !isEqual(this.currentData, this.persistedData);
                        Vue.set(this.$data, 'isDirty', dirty);
                        if (dirty && this.isSaved) {
                            Vue.set(this.$data, 'isSaved', false);
                        }
                    },
                    deep: true,
                },

                $v: {
                    handler() {
                        if (this.$v.currentData instanceof Object) {
                            for (let [key, $v] of Object.entries(this.$v.currentData)) {
                                if (key[0] !== '$' && $v.$dirty && !$v.$anyError) {
                                    $v.$reset();
                                }
                            }
                        }
                    },
                    deep: true,
                },
            },
        });

        this.$on('hook:beforeMount', () => {
            let validations = config.validations;

            if (validations instanceof Function) {
                validations = validations.call(component);
            }
            vm.$options.validations.currentData =  validations;

            vm.$mount(document.createElement('div')); // start lifecycle
            window.addEventListener('beforeunload', beforeUnload);
            beforeEachHandler = router.beforeEach(beforeRouteLeave);

            if (config.autoInit) {
                vm.$init();
            }
        });

        this.$on('hook:destroyed', () => {
            vm.$destroy();
            window.removeEventListener('beforeunload', beforeUnload);
            beforeEachHandler(); // destroy beforeEach handler
        });

        if (!options.computed) {
            options.computed = {};
        }

        options.computed.$form = () => {
            let data = {
                $isDirty: vm.$data.isDirty,
                $isSaving: vm.$data.isSaving,
                $isSaved: vm.$data.isSaved,
                $init: vm.$init,
                $submit: vm.$submit,
            };

            for (let [key, value] of Object.entries(vm.$data.currentData)) {
                Object.defineProperty(data, key, {
                    get() {
                        return value;
                    },
                    set(newValue) {
                        Vue.set(vm.$data.currentData, key, newValue);
                    },
                    enumerable: true,
                });
            }

            return data;
        };

        options.computed.$v = () => vm.$v.currentData;
    },
});